import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Header, VentasDetalle } from "../../components";
import { VentasMiListaClientesV2 } from "../../components/VentasMiListaClientesV2";
import CotizacionesGeneradas from "../CotizacionesGeneradas";
import MapaCalor from "../MapaCalor";
import Categorias from "./Categorias";
import { HubHeaderProvider } from "./HubHeaderContext";
import Pizarra from "./Pizarra";
import { VentasDataVersionProvider } from "./VentasDataVersionContext";
import ventasEjecutivoStyles from "../VentasEjecutivoV2/VentasEjecutivoV2.module.scss";
import styles from "./Ventas.module.scss";

const BASE = "/admin/ventas";

// Registro único de visualizaciones del hub. Para sumar una nueva (p. ej. habilitar Categorías)
// alcanza con completar su entrada acá: `path`/`Component` la vuelven navegable y la suman a las
// rutas; `disabled` la deja como marcador en gris. El grid 3×2 respeta este orden — Pizarra es la
// primera celda (vista principal, /admin/ventas redirige ahí).
// `props` inyecta props extra al componente de la tab (CRM reusa los estilos de filtro de
// VentasEjecutivoV2 tal cual — 10.1); `wrapperClass` envuelve la tab en un contenedor propio.
const VENTAS_TABS = [
  { key: "pizarra", label: "Pizarra", path: "pizarra", Component: Pizarra },
  { key: "detalle", label: "Detalle", path: "detalle", Component: VentasDetalle },
  {
    key: "crm",
    label: "CRM",
    path: "crm",
    Component: VentasMiListaClientesV2,
    props: { filterStyles: ventasEjecutivoStyles },
    wrapperClass: ventasEjecutivoStyles.containerVentasEjecutivo,
  },
  { key: "mapa-calor", label: "Mapa de Calor", path: "mapa-calor", Component: MapaCalor },
  { key: "cotizaciones", label: "Cotizaciones", path: "cotizaciones", Component: CotizacionesGeneradas },
  { key: "categorias", label: "Categorías", path: "categorias", Component: Categorias },
];

const Ventas = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const activeKey = VENTAS_TABS.find((t) => t.path && location.pathname === `${BASE}/${t.path}`)?.key;

  // Keep-alive: cada tab visitada se monta una vez y NO se desmonta al cambiar de tab (queda en el
  // DOM oculta con display:none y se vuelve a mostrar al volver). `visited` registra las tabs ya
  // montadas; una tab no visitada nunca se monta (no montamos las 4 de entrada). Como la instancia
  // persiste, sus queries no se re-disparan y sus filtros se conservan al volver.
  const [visited, setVisited] = useState(() => new Set());
  useEffect(() => {
    if (!activeKey) return;
    setVisited((prev) => (prev.has(activeKey) ? prev : new Set(prev).add(activeKey)));
  }, [activeKey]);

  // Regla del hub (Ignacio, 2026-09-21): el header es del SHELL, no de cada tab — inmaculado al
  // cambiar de tab. Cada tab publica sus filtros acá (useHubHeaderFilters, HubHeaderContext.js)
  // por un mecanismo que NO depende de llegar a ningún punto particular de su árbol de render (si
  // la tab corta con un loader antes de terminar de cargar, igual puede registrar `null` o los
  // filtros que ya tenga — nunca se salta el registro). El shell arma UN SOLO <Header>, siempre,
  // leyendo acá abajo los filtros de la tab activa. Antes cada tab armaba su propio <Header> vía
  // renderHeader(filtersContent) dentro de su propio render — si cortaba antes (ej. Mapa de Calor,
  // `if (loading) return <Loader fullPage />` antes de construir los filtros), ese <Header> nunca
  // se creaba y la pantalla se quedaba sin header mientras cargaba.
  const [filtersByTab, setFiltersByTab] = useState({});
  const registerFilters = useCallback((key, node) => {
    setFiltersByTab((prev) => (prev[key] === node ? prev : { ...prev, [key]: node }));
  }, []);

  // Ruta base /admin/ventas o subruta desconocida → a la Pizarra, la vista principal del hub (el
  // hub la resuelve; appRoutes NO lista /admin/ventas, ver regla 10.254). Va DESPUÉS de los hooks
  // (nunca condicionar hooks).
  if (!activeKey) return <Navigate to={`${BASE}/pizarra`} replace />;

  // Grid de visualizaciones que va en el leftContent del Header compartido, seguido de un separador
  // vertical sutil hacia los filtros (envueltos juntos en .vizGridWrapper — ver Ventas.module.scss).
  // La tab activa lo pinta en #2c3e50; las deshabilitadas quedan en gris, sin onClick.
  const grid = (
    <div className={styles.vizGridWrapper}>
      <div className={styles.vizGrid}>
        {VENTAS_TABS.map((tab) => {
          const isActive = tab.key === activeKey;
          const className = `${styles.vizCell} ${isActive ? styles.vizCellActive : ""} ${
            tab.disabled ? styles.vizCellDisabled : ""
          }`;
          return (
            <button
              key={tab.key}
              type="button"
              className={className}
              onClick={tab.disabled ? undefined : () => navigate(`${BASE}/${tab.path}`)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div className={styles.vizSeparator} />
    </div>
  );

  // Filtros de la tab activa — lo único que cambia en el header al cambiar de tab. Si esa tab
  // todavía no registró nada (primer render, antes de su primer efecto), el slot queda vacío pero
  // el <Header> ya existe con el logo y el grid — nunca desaparece.
  // centerLeftContent: alinea el grid en el mismo eje vertical que los filtros (prop opcional del
  // Header, default false en todas las demás pantallas — ver Header/index.js).
  const activeFilters = filtersByTab[activeKey] || null;

  // Keep-alive: se renderizan solo las tabs YA visitadas (o la activa en su primer render). Cada una
  // queda montada; la inactiva se oculta con display:none (.tabHidden) — no ocupa espacio ni captura
  // clicks/foco. El <Header> vive FUERA de las tabs (un solo mount para toda la vida del hub) — no
  // se oculta ni se recrea al cambiar de tab, solo cambian los filtros que le llegan por contexto.
  //
  // Marco de página (regresión 2026-09-21, ver Ventas.module.scss .wrapper): el <Header> y el área
  // de tabs van DENTRO del mismo marco de 15px que EstadoCuenta.wrapper — antes el <Header> quedaba
  // afuera de cualquier wrapper con padding. Las tabs siguen aportando SU wrapper (contienen el
  // resto del layout que ya tenían, `min-height`, etc.) pero anulan su propio padding:15px cuando
  // reciben tabKey — el marco de página se aporta UNA sola vez, acá.
  return (
    <div className={styles.wrapper}>
      {/* Sticky (2026-09-22): wrapper puro, sin padding/margen propio — mide exactamente lo mismo
          que <Header> desnudo, así que su apariencia no cambia ni un pixel; solo se agrega
          position/top/z-index/fondo (ver .stickyHeader). El ResizeObserver que medía su alto real
          para publicarlo como --ventas-header-height se retiró (2026-09-22, fix scroll de la
          Pizarra): esa variable se quedó sin ningún consumidor — el tablero de la Pizarra mide su
          propia posición directamente ahora, ver Pizarra/index.js. */}
      <div className={styles.stickyHeader}>
        <Header leftContent={grid} expandChildren centerLeftContent>
          {activeFilters}
        </Header>
      </div>
      <HubHeaderProvider value={registerFilters}>
        {/* Invalidación cross-tab (2026-09-22, ver VentasDataVersionContext.js y
            docs/modulos/ventas-hub.md): DENTRO de HubHeaderProvider, mismo alcance (todo el hub,
            nunca fuera de él). `active` es la señal real de visibilidad que cada tab usa para
            decidir si tiene que recargar — key===activeKey, no el `display:none` de .tabHidden
            (una tab puede seguir MONTADA sin ser la activa). */}
        <VentasDataVersionProvider>
          {VENTAS_TABS.filter((tab) => tab.Component && (visited.has(tab.key) || tab.key === activeKey)).map(
            ({ key, Component, props, wrapperClass }) => {
              const el = <Component tabKey={key} active={key === activeKey} {...(props || {})} />;
              return (
                <div key={key} className={key === activeKey ? undefined : styles.tabHidden}>
                  {wrapperClass ? (
                    <div className={wrapperClass} style={{ padding: 0 }}>
                      {el}
                    </div>
                  ) : (
                    el
                  )}
                </div>
              );
            }
          )}
        </VentasDataVersionProvider>
      </HubHeaderProvider>
    </div>
  );
};

export default Ventas;
